/**
 * Shows the "Neuigkeiten" once per sign-in when there are any.
 *
 * Real session: the API names what this person has not acknowledged
 * (GET /me/announcements); acknowledging (after the last card) is stored
 * per person on the server, so web and app agree.
 * Demo: a small sample from the device (the newest entries), shown only once
 * the persona's tour is over — the tour comes first, and marketing gets to
 * show the cards at the end of it.
 *
 * A real session's welcome (tour/WelcomeGate.tsx) comes first.
 *
 * Skew-safe: only entries this bundle has words for are shown and
 * acknowledged; anything newer stays pending for the next build.
 */
import { useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { announcementsWithWords, newsMayShow } from '@fsv/data';
import { useBackend, useSession } from '../data/hooks';
import { useTour } from '../tour/TourProvider';
import { useWelcomeDue } from '../tour/WelcomeGate';
import { WhatsNew } from './WhatsNew';

/** Closed without finishing: not again until the page is reloaded (then they come back, unacknowledged). */
const closedThisPage = new Set<string>();

export function NewsGate() {
  const backend = useBackend();
  const session = useSession();
  const tour = useTour();
  const qc = useQueryClient();
  const who = `${backend.kind}:${session.me.memberId}`;
  const [closed, setClosed] = useState(() => closedThisPage.has(who));
  const finished = useRef(false);
  // The welcome (0015) comes first; the cards wait until it is answered.
  const welcomeDue = useWelcomeDue();
  const enabled = !closed && welcomeDue === false && newsMayShow(backend.kind, session.mode, tour.available && tour.progress?.status === 'done');
  const query = useQuery({
    queryKey: ['fsv', backend.kind, 'news', session.me.memberId],
    queryFn: () => backend.announcements(),
    enabled,
    staleTime: Infinity,
    retry: false,
  });
  const items = useMemo(() => announcementsWithWords(query.data ?? []), [query.data]);
  if (!enabled || items.length === 0) return null;
  return (
    <WhatsNew
      items={items}
      onDone={async () => {
        finished.current = true;
        await backend.acknowledgeAnnouncements(items.map((item) => item.id));
        await qc.invalidateQueries({ queryKey: ['fsv', backend.kind, 'news'] });
      }}
      onClose={() => {
        // Closed half way: not again on this page. Finished: acknowledged, nothing to remember.
        if (!finished.current) closedThisPage.add(who);
        setClosed(true);
      }}
    />
  );
}
